import { AsyncBoundary } from "@components/AsyncBoundary";
import { useFrontDeskTodos } from "@frontdesk/TodosBand";
import { inboundCallerName } from "@notifications/inboundCallerName";
import {
  type IncomingCall,
  type LiveCallStatus,
  useIncomingCallStore,
} from "@notifications/incomingCallStore";
import { useHeldFor } from "@notifications/useHeldFor";
import { patientsClient } from "@patients/patientsClient";
import { signalmentOf } from "@patients/signalment";
import { formatSeconds } from "@phone/phoneCallOutcome";
import { formatPhoneNumber } from "@phone/phoneSetupClient";
import { PatientSprite } from "@scribenote/brian-design-system";
import { useQuery } from "@tanstack/react-query";
import { callbackLabel, isCallback } from "@todos/todoFields";
import { useEffect, useState } from "react";

const STATE: Record<LiveCallStatus, string> = {
  ringing: "Ringing",
  disclosing: "Brian answered",
  handling: "Brian is on the call",
  dialing: "Ringing the clinic",
  out_dialing_staff: "Calling out",
  out_awaiting_confirmation: "Calling out",
  out_dialing_recipient: "Calling out",
  out_disclosing: "Connecting",
  out_connecting: "Connecting",
  bridged: "On the call",
};

const LABEL =
  "font-mono text-[0.6875rem] uppercase tracking-[0.16em] text-ink-on-nav-muted";

/** Inbound `started_at` is when the caller reached the provider. */
const useOnTheLine = (call: IncomingCall): string | null => {
  const active = call.status === "bridged" || call.status === "handling";
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const tick = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(tick);
  }, [active]);
  if (!active) return null;
  const from = call.startedAt
    ? new Date(call.startedAt).getTime()
    : call.bridgedAtMs;
  if (from === null || !Number.isFinite(from)) return null;
  return formatSeconds(Math.max(0, Math.round((now - from) / 1000)));
};

const StateLine = ({
  call,
  waiting,
}: {
  call: IncomingCall;
  waiting: number;
}) => {
  const heldFor = useHeldFor(call.holdState === "none" ? null : call.heldAt);
  const onTheLine = useOnTheLine(call);
  const parts =
    call.holdState === "none"
      ? [STATE[call.status], onTheLine]
      : ["On hold", heldFor];
  if (waiting > 0) parts.push(`${waiting} more waiting`);
  return <>{parts.filter(Boolean).join(" · ")}</>;
};

const useCallerPatient = (call: IncomingCall) => {
  const { data: patients } = useQuery({
    queryKey: ["client-patients", call.clientUuid],
    queryFn: () => patientsClient.listForClient(call.clientUuid!),
    enabled: call.clientUuid !== null,
  });
  const named = call.clientPatients ?? [];
  const patient =
    named.length === 1
      ? patients?.find((p) => p.name === named[0])
      : patients?.length === 1 && named.length === 0
        ? patients[0]
        : undefined;
  return {
    patient,
    signalment: patient ? signalmentOf(patient.signalment) : undefined,
  };
};

const CallerCell = ({ call }: { call: IncomingCall }) => {
  const outbound = call.origin === "staff_outbound";
  const name = outbound
    ? (call.clientName ?? call.callerName)
    : inboundCallerName(call);
  const e164 = outbound ? call.recipientE164 : call.fromNumber;
  const number = e164 ? formatPhoneNumber(e164) : null;
  const title =
    [name ?? "Unknown caller", number !== name ? number : null]
      .filter(Boolean)
      .join(" · ") || "Unknown caller";
  const { patient, signalment } = useCallerPatient(call);
  const patientLine = patient
    ? [patient.name, signalment?.breed ?? signalment?.species, signalment?.age]
        .filter(Boolean)
        .join(" · ")
    : call.clientPatients?.join(", ");

  return (
    <div
      data-dd-privacy="hidden"
      className="flex min-w-0 items-center gap-3 rounded-[6px] border-[1.5px] border-accent bg-nav-raised px-3.5 py-2.5"
    >
      <PatientSprite
        sprite={patient?.sprite}
        size={44}
        className="size-[34px]"
      />
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-display text-lg font-medium text-ink-on-nav">
          {title}
        </span>
        {patientLine && (
          <span className="truncate text-[0.8125rem] text-ink-on-nav-muted">
            {patientLine}
          </span>
        )}
      </span>
    </div>
  );
};

const CallbackCell = () => {
  const callback = useFrontDeskTodos().find(isCallback);
  if (!callback) return null;
  return (
    <div className="flex min-w-0 flex-col" data-dd-privacy="hidden">
      <span className={LABEL}>Callback</span>
      <span className="truncate text-sm text-ink-on-nav">
        {callbackLabel(callback)}
      </span>
    </div>
  );
};

export const PhoneDock = () => {
  const live = useIncomingCallStore((s) => s.calls).filter((c) => !c.settled);
  if (live.length === 0) return null;
  const call = live[live.length - 1];

  return (
    <section
      aria-label="Phone"
      aria-live="polite"
      className="grid shrink-0 grid-cols-[auto_minmax(0,1.6fr)_minmax(0,1fr)] items-center gap-5 rounded-lg bg-nav px-[22px] py-3.5 text-ink-on-nav"
    >
      <div className="flex flex-col">
        <span className={LABEL}>Phone</span>
        <span className="whitespace-nowrap text-sm">
          <StateLine call={call} waiting={live.length - 1} />
        </span>
      </div>
      <CallerCell key={call.callUuid} call={call} />
      <AsyncBoundary skeleton={null} fallback={() => null}>
        <CallbackCell />
      </AsyncBoundary>
    </section>
  );
};
